import type React from "react";
import { useMemo } from "react";

import type { ITableProps } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";

import type { ISwitcherConfig } from "app/components/React/WidgetContainer/types";
import type { IFooterButton } from "app/components/React/WidgetContainer/WidgetFooter";
import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";

import GenericWidgetSearchOverview from "../GenericWidget/GenericWidgetSearchOverview";

import NoDataEmptyState from "./NoDataEmptyState";

interface GenericWidgetTableProps {
    title: string;
    tooltip: string;
    tableProps: ITableProps;
    footerButtonsConfig?: IFooterButton[];
    subtitleFilters?: Filter[];
    switcherConfig?: ISwitcherConfig;
    minHeight?: number;
    TableTop?: React.ReactNode;
    isError?: boolean;
    noDataText?: { title?: string; subtitle?: string };
    contentHeight?: number;
}

const GenericWidgetTable: React.FC<GenericWidgetTableProps> = ({
    title,
    tooltip,
    footerButtonsConfig,
    subtitleFilters,
    switcherConfig,
    minHeight,
    TableTop,
    tableProps,
    isError,
    noDataText,
    contentHeight,
}) => {
    const {
        data,
        isLoading,
        columnConfig,
        sticky = false,
        theme = "white",
        rowHoverable = false,
        bordersConfig = { body: false, groupBorder: true, header: false },
        onSort,
        pagination,
        expandable,
        loaderRowCount = 5,
    } = tableProps;

    const tableKey = useMemo(() => {
        return `table-${columnConfig.length}-${columnConfig.map((col) => col.key).join(",")}`;
    }, [columnConfig]);

    const renderEmptyState = () => (
        <NoDataEmptyState
            title={noDataText?.title}
            subtitle={noDataText?.subtitle}
            isError={isError}
            height={contentHeight}
        />
    );

    return (
        <GenericWidgetSearchOverview
            title={title}
            tooltip={tooltip}
            subtitleFilters={subtitleFilters}
            footerButtonsConfig={data.length ? footerButtonsConfig : undefined}
            switcherConfig={switcherConfig}
            minHeight={minHeight}
        >
            {TableTop}
            <Table
                key={tableKey}
                theme={theme}
                bordersConfig={bordersConfig}
                columnConfig={columnConfig}
                data={data}
                onSort={onSort}
                isLoading={isLoading}
                loaderRowCount={loaderRowCount}
                sticky={sticky}
                rowHoverable={rowHoverable}
                noData={{ Component: renderEmptyState }}
                pagination={pagination}
                expandable={expandable}
            />
        </GenericWidgetSearchOverview>
    );
};

export default GenericWidgetTable;
